/** The execution status from whichever branch of the result is populated. */
function statusOf(res) {
    var _a, _b, _c, _d;
    const branch = (_a = res.Transaction) !== null && _a !== void 0 ? _a : res.FailedTransaction;
    // Prefer effects.status — that's where gRPC read results (waitForTransaction /
    // simulateTransaction) carry it. Fall back to the branch-level status, which is where
    // dApp Kit's sign result puts it, and whose `effects` can be null when the SDK fails to
    // parse the effects BCS.
    return (_d = (_c = (_b = branch === null || branch === void 0 ? void 0 : branch.effects) === null || _b === void 0 ? void 0 : _b.status) !== null && _c !== void 0 ? _c : branch === null || branch === void 0 ? void 0 : branch.status) !== null && _d !== void 0 ? _d : null;
}
/** The parsed error object from a failed result, or null. */
function errorOf(res) {
    const status = statusOf(res);
    if (!status || status.success)
        return null;
    const err = status.error;
    if (err == null)
        return null;
    return typeof err === 'string' ? { message: err } : err;
}
/** True when the transaction executed successfully on-chain. */
export function isTxSuccess(res) {
    var _a;
    return ((_a = statusOf(res)) === null || _a === void 0 ? void 0 : _a.success) === true;
}
/** A human-readable on-chain error message, or '' when the tx succeeded. */
export function getTxErrorMessage(res) {
    var _a;
    const status = statusOf(res);
    if (!status || status.success)
        return '';
    // Failed, but with no error payload — report something rather than ''.
    const err = errorOf(res);
    if (err == null)
        return 'Transaction failed';
    return (_a = err.message) !== null && _a !== void 0 ? _a : JSON.stringify(err);
}
// Known phrasings the node uses for an insufficient-gas execution error. Word-bounded
// rather than a bare /gas/ substring test, which also matched Move aborts and unrelated
// errors that merely mention gas somewhere in their message. Not anchored to the start:
// the description is server-generated, and anchoring would silently stop matching if it
// ever gained a prefix — which would break the accumulator flow below.
const INSUFFICIENT_GAS_PATTERNS = [
    // 'Insufficient Gas.' (ExecutionFailureStatus Display), 'INSUFFICIENT_GAS',
    // 'InsufficientGas' — whichever form the transport surfaces.
    /\binsufficient[\s_-]*gas\b/i,
    // Separators optional: 'GasBalanceTooLow' / 'GAS_BALANCE_TOO_LOW' / 'gas balance too low'.
    // A missed match here returns false and blocks an address-balance account at validation,
    // so this must stay at least as permissive as errorMaps.ts's GAS_ERROR_PATTERNS.
    /\bgas[\s_-]*balance[\s_-]*too[\s_-]*low\b/i,
];
/**
 * `$kind` tags that carry structured error detail. These are genuine execution failures, so
 * they never count as a gas error no matter what their message says.
 *
 * This is the full set of non-'Unknown' variants of `SuiClientTypes.ExecutionError`. An
 * insufficient-gas error provably is NOT one of them: it carries no `errorDetails` oneof, so
 * `parseGrpcExecutionError` (@mysten/sui, grpc/core.mjs) falls to its `default` branch and
 * tags it `'Unknown'`, discarding the `ExecutionErrorKind` enum — which is why the message
 * is all we have to match on.
 */
const STRUCTURED_ERROR_KINDS = new Set([
    'MoveAbort',
    'SizeError',
    'CommandArgumentError',
    'TypeArgumentError',
    'PackageUpgradeError',
    'IndexError',
    'CoinDenyListError',
    'CongestedObjects',
    'ObjectIdError',
]);
/**
 * True when a failed result is an insufficient-gas failure from the client-built dry-run.
 *
 * This is expected for accounts whose SUI lives in the address-balance accumulator (no SUI
 * coin objects to pay gas): the client can't resolve a gas payment, so the simulation
 * reports insufficient gas even though the wallet supplies gas from the address balance at
 * execution time. Callers therefore must not block on it.
 *
 * Deliberately narrow — it matches ONLY an insufficient-gas error with no structured error
 * detail. Structured variants ($kind 'MoveAbort', 'CommandArgumentError', …) are real
 * failures and never match, even if their message mentions gas.
 *
 * Known limitation: an ordinary account that is genuinely out of gas produces the same
 * error, so it is waved through here too and surfaces at sign/execute instead of during
 * validation. The two are indistinguishable from the result alone — the SDK discards the
 * ExecutionErrorKind enum for detail-less errors, leaving only the message.
 */
export function isGasError(res) {
    var _a;
    const err = errorOf(res);
    if (!err)
        return false;
    // Deny-list rather than requiring $kind === 'Unknown': a tag we haven't anticipated must
    // fall through to the message test. Requiring 'Unknown' would fail in the dangerous
    // direction — an unexpected tag on a real insufficient-gas error would block an
    // accumulator-balance user whose transaction would have executed fine.
    if (err.$kind != null && STRUCTURED_ERROR_KINDS.has(err.$kind))
        return false;
    const message = (_a = err.message) !== null && _a !== void 0 ? _a : '';
    return INSUFFICIENT_GAS_PATTERNS.some((pattern) => pattern.test(message));
}
/**
 * Id of an object CREATED by the transaction whose Move type equals `type`.
 * gRPC `effects.changedObjects` don't carry the object type, so the caller must
 * request `include: { effects: true, objectTypes: true }` on waitForTransaction.
 */
export function getCreatedObjectIdByType(res, type) {
    var _a, _b, _c;
    const tx = res.Transaction;
    const objectTypes = (_a = tx === null || tx === void 0 ? void 0 : tx.objectTypes) !== null && _a !== void 0 ? _a : {};
    const changed = (_c = (_b = tx === null || tx === void 0 ? void 0 : tx.effects) === null || _b === void 0 ? void 0 : _b.changedObjects) !== null && _c !== void 0 ? _c : [];
    for (const obj of changed) {
        if (obj.idOperation === 'Created' && objectTypes[obj.objectId] === type) {
            return obj.objectId;
        }
    }
    return null;
}
